import { Button, Form, FormGroup, Nav, Tab } from "react-bootstrap";
import { useState } from "react";
import CustomEditor from "../../elements/CustomEditor";
import { Faq, FaqTranslation } from "../../interfaces/Faq";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES, Language } from "../../../utils/data/Languages";
import { Overlay } from "../../elements/Overlay";

export interface FaqEditProps {
    faq: Faq;
    submitCallback: CallableFunction;
    closeCallback: CallableFunction;
}

const emptyFaqTranslation: FaqTranslation = {
    lang: 'en',
    title: '',
    body: ''
};

export default function FaqEdit(props: FaqEditProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // State for translations by language
    const [currentLang, setCurrentLang] = useState<Language>(props.faq.defaultLang);
    const [translations, setTranslations] = useState<{ [key in Language]?: FaqTranslation }>(() => {
        const translationsMap: { [key in Language]?: FaqTranslation } = {};
        props.faq.translations.forEach(trans => {
            translationsMap[trans.lang] = { ...trans };
        });
        return translationsMap;
    });

    // State for translation loading
    const [translatingTitle, setTranslatingTitle] = useState(false);
    const [translatingBody, setTranslatingBody] = useState(false);

    const handleClose = () => {
        props.closeCallback();
    }

    const handleSubmit = (event: any) => {
        event.preventDefault();
        let error = false;
        let error_messages: string[] = [];

        // Validate that at least one translation exists
        const translationArray: FaqTranslation[] = Object.values(translations).filter(t => t !== undefined) as FaqTranslation[];
        if (translationArray.length === 0) {
            error = true;
            error_messages.push(t("Please add at least one translation"));
        }

        if (error) {
            alert(error_messages.join('\n'));
        } else {
            const newFaq: Faq = {
                ...props.faq,
                defaultLang: props.faq.defaultLang,
                weight: parseInt(event.target.weight.value) || 0,
                translations: translationArray
            };

            const requestOptionsPost = {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('token')
                },
                body: JSON.stringify(newFaq)
            };

            fetch(import.meta.env.VITE_API_URL + '/v1/faq/' + props.faq._id, requestOptionsPost)
                .then(async response => {
                    if (response.status !== 200) {
                        const text = await response.text();
                        console.error('Error response:', text);
                        throw new Error(`Server error: ${response.status}`);
                    }
                    // API returns just the ID as plain text, not JSON
                    const text = await response.text();
                    return { id: text.trim() };
                })
                .then(data => {
                    props.submitCallback(newFaq);
                })
                .catch(error => {
                    console.error('Error updating FAQ:', error);
                });
        }
    }

    const updateTranslationField = (lang: Language, field: keyof FaqTranslation, value: string) => {
        setTranslations(prev => ({
            ...prev,
            [lang]: {
                ...(prev[lang] || { ...emptyFaqTranslation, lang }),
                [field]: value
            }
        }));
    }

    const handleLanguageTabChange = (lang: string | null) => {
        if (lang) {
            setCurrentLang(lang as Language);
            // Ensure translation exists for this language
            if (!translations[lang as Language]) {
                setTranslations(prev => ({
                    ...prev,
                    [lang]: { ...emptyFaqTranslation, lang: lang as Language }
                }));
            }
        }
    }

    const translateWithDeepL = async (text: string, targetLang: Language, field: 'title' | 'body') => {
        if (!text.trim()) {
            alert(t("Please enter text to translate"));
            return;
        }

        if (targetLang === 'en') {
            alert(t("Cannot translate to English (source language)"));
            return;
        }

        const setLoading = field === 'title' ? setTranslatingTitle : setTranslatingBody;
        setLoading(true);

        try {
            const response = await fetch(import.meta.env.VITE_API_URL + '/v1/translate', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: text,
                    targetLang: targetLang,
                    sourceLang: 'en'
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Translation failed');
            }

            const result = await response.json();
            updateTranslationField(targetLang, field, result.translatedText);

        } catch (error: any) {
            console.error('Translation error:', error);
            alert(t("Translation error: ") + error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleTranslateTitle = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.title) {
            translateWithDeepL(englishTranslation.title, currentLang, 'title');
        } else {
            alert(t("Please enter English title first"));
        }
    }

    const handleTranslateBody = () => {
        const englishTranslation = translations['en'];
        if (englishTranslation && englishTranslation.body) {
            translateWithDeepL(englishTranslation.body, currentLang, 'body');
        } else {
            alert(t("Please enter English body first"));
        }
    }

    const doNothing = () => {

    }

    return(
        <Overlay title={t("Edit FAQ")} onClose={handleClose} className="admin-edit-faq-container">
            <div className="faq-item-form-container">
                <Form onSubmit={ handleSubmit }>
                    <FormGroup className="weight" controlId="weight">
                        <Form.Label aria-required={ true }>{ t("Weight") }</Form.Label>
                        <Form.Control type="number" required={ true } defaultValue={ props.faq.weight }
                            placeholder={ t("Enter a weight") } />
                    </FormGroup>

                    <Tab.Container activeKey={currentLang} onSelect={handleLanguageTabChange}>
                        <Nav variant="tabs">
                            {LANGUAGES.map(lang => (
                                <Nav.Item key={lang.code}>
                                    <Nav.Link eventKey={lang.code}>{lang.name}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                        <Tab.Content style={{ marginTop: '20px' }}>
                            {LANGUAGES.map(lang => {
                                const trans = translations[lang.code] || { ...emptyFaqTranslation, lang: lang.code };
                                const isEnglish = lang.code === 'en';
                                const showTranslateLink = !isEnglish && lang.code !== 'ca';

                                return (
                                    <Tab.Pane key={lang.code} eventKey={lang.code}>
                                        <FormGroup className="title" controlId={`title-${lang.code}`}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Form.Label>{ t("Title") }</Form.Label>
                                                {showTranslateLink && (
                                                    <a
                                                        href="#"
                                                        onClick={(e) => { e.preventDefault(); handleTranslateTitle(); }}
                                                        style={{ fontSize: '0.9em', marginLeft: '10px' }}
                                                    >
                                                        {translatingTitle ? t("Translating...") : t("Translate with DeepL")}
                                                    </a>
                                                )}
                                            </div>
                                            <Form.Control
                                                type="text"
                                                value={trans.title}
                                                onChange={(e) => updateTranslationField(lang.code, 'title', e.target.value)}
                                                placeholder={ t("Enter a title") }
                                                disabled={translatingTitle} />
                                        </FormGroup>
                                        <div className="field field-body">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                <label>{ t("Body") }</label>
                                                {showTranslateLink && (
                                                    <a
                                                        href="#"
                                                        onClick={(e) => { e.preventDefault(); handleTranslateBody(); }}
                                                        style={{ fontSize: '0.9em', marginLeft: '10px' }}
                                                    >
                                                        {translatingBody ? t("Translating...") : t("Translate with DeepL")}
                                                    </a>
                                                )}
                                            </div>
                                            <CustomEditor
                                                change={(data: string) => updateTranslationField(lang.code, 'body', data)}
                                                changeFormatted={ doNothing }
                                                data={ trans.body }
                                                formatted={ '' }
                                                id={`body-${lang.code}`}
                                                label=""
                                                npl={ false }
                                                task_id={ '' }
                                            />
                                        </div>
                                    </Tab.Pane>
                                );
                            })}
                        </Tab.Content>
                    </Tab.Container>

                    <div className="actions">
                        <Button type="submit" variant="primary">{ t("Save") }</Button>
                    </div>
                </Form>
            </div>
        </Overlay>
    );
}
